import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const DUST_COUNT = 72;
const NEAR_Z = -1.2;
const FAR_Z = -16;

interface DustSeed {
  x: number;
  y: number;
  z: number;
  speed: number;
  length: number;
}

function seededRandom(seed: number) {
  let value = seed >>> 0;
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 4294967296;
  };
}

export function VelocityDust() {
  const groupRef = useRef<THREE.Group | null>(null);
  const materialRef = useRef<THREE.LineBasicMaterial | null>(null);
  const seeds = useMemo(() => {
    const random = seededRandom(0x91a7c3);
    return Array.from({ length: DUST_COUNT }, (): DustSeed => ({
      x: (random() - 0.5) * 15,
      y: (random() - 0.5) * 9,
      z: FAR_Z + random() * (NEAR_Z - FAR_Z),
      speed: 0.65 + random() * 1.35,
      length: 0.025 + random() * 0.09,
    }));
  }, []);
  const positions = useMemo(() => new Float32Array(DUST_COUNT * 6), []);
  const geometry = useMemo(() => {
    const value = new THREE.BufferGeometry();
    value.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return value;
  }, [positions]);

  useFrame(({ camera }, delta) => {
    const group = groupRef.current;
    const material = materialRef.current;
    if (!group || !material) return;

    group.position.copy(camera.position);
    group.quaternion.copy(camera.quaternion);

    const reduced = window.__planetReducedMotion === true;
    const velocity = reduced ? 0 : Math.abs(window.__planetScrollVelocity ?? 0);
    const travel = (0.18 + velocity * 9.5) * Math.min(delta, 1 / 30);
    const streak = 1 + velocity * 42;

    seeds.forEach((dust, index) => {
      if (!reduced) dust.z += travel * dust.speed;
      if (dust.z > NEAR_Z) dust.z = FAR_Z;
      const offset = index * 6;
      positions[offset] = dust.x;
      positions[offset + 1] = dust.y;
      positions[offset + 2] = dust.z;
      positions[offset + 3] = dust.x;
      positions[offset + 4] = dust.y;
      positions[offset + 5] = dust.z - dust.length * streak;
    });
    geometry.attributes.position.needsUpdate = true;

    const targetOpacity = reduced ? 0.08 : 0.12 + velocity * 0.42;
    material.opacity = THREE.MathUtils.lerp(material.opacity, targetOpacity, 0.1);
  });

  return (
    <group ref={groupRef} name="velocity-dust">
      <lineSegments geometry={geometry} frustumCulled={false} raycast={() => null}>
        <lineBasicMaterial
          ref={materialRef}
          color="#f5f0e8"
          transparent
          opacity={0.12}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </lineSegments>
    </group>
  );
}

export default VelocityDust;
